// Learn → Stories (contracts/learn.md §1): every story with its status, category, cover, when it goes or went
// live, the players' numbers (views, completions, the furthest slide on average, the lesson push) and what still
// blocks publishing. Rows open the editor (/learn/:id); editors create new stories.

import { useState } from "react";
import { useNavigate } from "react-router";
import { type AdminStory, CATEGORY_LABEL, STATUS_TONE, STORY_STATUSES, type StoryStatus, useStories } from "../api/learn";
import { useAuth, useCan } from "../auth/AuthContext";
import { tableStyles } from "../components/table";
import { Button, Card, Chip, EmptyState, ErrorState, Loading, PageHeader, Segmented } from "../components/ui";
import { formatDateTime } from "../lib/dates";
import { fmtInt, fmtPct } from "../lib/format";
import l from "./learn/learn.module.css";
import styles from "./pages.module.css";

type Filter = "all" | StoryStatus;

function when(s: AdminStory): string {
  if (s.status === "published" && s.publishedAt) return `published ${formatDateTime(s.publishedAt)}`;
  if (s.status === "scheduled" && s.publishAt) return `goes live ${formatDateTime(s.publishAt)}`;
  if (s.status === "archived") return s.publishedAt ? `was published ${formatDateTime(s.publishedAt)}` : "archived";
  return `updated ${formatDateTime(s.updatedAt)}`;
}

export function LearnPage() {
  const canEdit = useCan("stories.edit");
  const { admin } = useAuth();
  const navigate = useNavigate();
  const list = useStories();
  const [filter, setFilter] = useState<Filter>("all");
  const items = list.data?.items ?? [];
  const shown = filter === "all" ? items : items.filter((s) => s.status === filter);
  const count = (f: Filter) => (f === "all" ? items.length : items.filter((s) => s.status === f).length);

  return (
    <>
      <PageHeader
        title="Learn"
        meta={
          canEdit ? (
            <span>Short swipeable stories that teach how to play and how to get better. Publishing one sends a lesson push.</span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Read-only · editing stories needs the editor role (you are {admin?.role})
            </span>
          )
        }
        actions={
          canEdit ? (
            <Button variant="primary" onClick={() => navigate("/learn/new")} data-testid="new-story">
              New story
            </Button>
          ) : null
        }
      />
      <div className={l.filters}>
        <Segmented
          label="Status"
          value={filter}
          onChange={setFilter}
          options={(["all", ...STORY_STATUSES] as const).map((f) => ({ value: f, label: `${f === "all" ? "All" : f[0]!.toUpperCase() + f.slice(1)} · ${count(f)}` }))}
        />
      </div>
      <Card padded={false}>
        {list.isPending ? (
          <Loading rows={6} />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : shown.length === 0 ? (
          <EmptyState title={filter === "all" ? "No stories yet" : `No ${filter} stories`}>{canEdit ? "Create one with “New story”." : "Editors write them here."}</EmptyState>
        ) : (
          <div className={tableStyles.wrap}>
            <table className={tableStyles.table}>
              <thead>
                <tr>
                  <th>Story</th>
                  <th>Status</th>
                  <th>Players</th>
                  <th>Before it can go out</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((s) => (
                  <tr key={s.id} data-clickable data-testid="story-row" data-status={s.status} onClick={(e) => !(e.target as HTMLElement).closest("a, button") && navigate(`/learn/${s.id}`)}>
                    <td>
                      <div className={l.cell}>
                        <span className={l.circle} data-unseen={s.status === "published" || undefined}>
                          {s.cover ? <img src={s.cover.url} alt="" data-testid="story-cover" /> : <span className={l.circleEmpty}>none</span>}
                        </span>
                        <div className={l.cellText}>
                          <button type="button" className={l.rowTitle} onClick={() => navigate(`/learn/${s.id}`)}>
                            {s.content.en?.title || "Untitled"}
                          </button>
                          <span>{s.content.en?.subtitle ?? ""}</span>
                          <span className={l.meta}>
                            {CATEGORY_LABEL[s.category]} · {s.slides.length} {s.slides.length === 1 ? "slide" : "slides"} · {Object.keys(s.content).length}{" "}
                            {Object.keys(s.content).length === 1 ? "language" : "languages"}
                            {s.puzzleTypes.length ? ` · ${s.puzzleTypes.join(", ")}` : ""}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Chip tone={STATUS_TONE[s.status]}>{s.status}</Chip>
                      <span className={l.when}>{when(s)}</span>
                    </td>
                    <td>
                      <dl className={l.stats} data-testid="story-stats">
                        <div>
                          <dt>views</dt>
                          <dd>{fmtInt(s.stats.views)}</dd>
                        </div>
                        <div>
                          <dt>done</dt>
                          <dd>
                            {fmtInt(s.stats.completed)} · {fmtPct(s.stats.completionRate)}
                          </dd>
                        </div>
                        <div>
                          <dt>avg slide</dt>
                          <dd>{s.stats.avgSlide ?? "—"}</dd>
                        </div>
                        <div>
                          <dt>pushed</dt>
                          <dd>{s.push ? fmtInt(s.stats.pushed) : "off"}</dd>
                        </div>
                      </dl>
                    </td>
                    <td>
                      {s.problems.length ? (
                        <ul className={l.problems} data-testid="story-problems">
                          {s.problems.slice(0, 3).map((p) => (
                            <li key={p}>{p}</li>
                          ))}
                          {s.problems.length > 3 ? <li>+{s.problems.length - 3} more</li> : null}
                        </ul>
                      ) : (
                        <span className={l.ready}>Ready</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
